'use client';

import { getPromotionalCatalogs } from '@/actions/catalogs';
import type {
  PromotionalCatalogNavItem,
  PromotionalCatalogSource,
} from '@/actions/catalogs/types';
import { useLocale } from '@/hooks/useLocale';
import { getTranslation } from '@/lib/utils';
import { pugliaMap, sicilyMap } from '@/utils/maps';
import { useEffect, useMemo } from 'react';
import { create } from 'zustand';

type State = {
  sicily: PromotionalCatalogSource[];
  puglia: PromotionalCatalogSource[];
  isLoading: boolean;
  hasFetched: boolean;
  isFetching: boolean;
  load: () => Promise<void>;
};

const useStore = create<State>()((set, get) => ({
  sicily: [],
  puglia: [],
  isLoading: false,
  hasFetched: false,
  isFetching: false,
  load: async () => {
    const { hasFetched, isFetching } = get();

    if (hasFetched || isFetching) {
      return;
    }

    set({ isLoading: true, isFetching: true });

    try {
      const { sicily, puglia } = await getPromotionalCatalogs();
      set({
        sicily,
        puglia,
        isLoading: false,
        hasFetched: true,
        isFetching: false,
      });
    } catch {
      set({
        sicily: [],
        puglia: [],
        isLoading: false,
        hasFetched: false,
        isFetching: false,
      });
    }
  },
}));

function mapToNavItems(
  catalogs: PromotionalCatalogSource[],
  lang: Locale,
): PromotionalCatalogNavItem[] {
  return catalogs
    .map((catalog) => {
      const { slug, title } = getTranslation(catalog.translate ?? [], lang);

      if (!slug || !title) {
        return null;
      }

      const region =
        catalog.location === 'puglia' ? pugliaMap[lang] : sicilyMap[lang];
      const href =
        lang === 'en' ? `/${region}/${slug}` : `/${lang}/${region}/${slug}`;

      return { title, href };
    })
    .filter((item): item is PromotionalCatalogNavItem => item !== null);
}

export function usePromotionalCatalogs() {
  const lang = useLocale();
  const sicilySources = useStore((state) => state.sicily);
  const pugliaSources = useStore((state) => state.puglia);
  const isLoading = useStore((state) => state.isLoading);

  const sicily = useMemo(
    () => mapToNavItems(sicilySources, lang),
    [sicilySources, lang],
  );
  const puglia = useMemo(
    () => mapToNavItems(pugliaSources, lang),
    [pugliaSources, lang],
  );

  return { sicily, puglia, isLoading };
}

export function useLoadPromotionalCatalogs() {
  const load = useStore((state) => state.load);

  useEffect(() => {
    load();
  }, [load]);
}
